/**
 * Entity Analytics Panel
 *
 * Display analytics for entity clicks and engagement with visualizations
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { authFetch } from '@/utils/auth-fetch';
import { Loader2, TrendingUp, MousePointerClick, Eye, BarChart3 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface EntityAnalytics {
  entity_id: string;
  entity_type: string;
  entity_name: string;
  total_clicks: number;
  unique_users: number;
  avg_time_on_page: number;
  conversion_rate: number;
  last_clicked: string;
}

interface EntityAnalyticsPanelProps {
  siteId: string;
}

export function EntityAnalyticsPanel({ siteId }: EntityAnalyticsPanelProps) {
  const [analytics, setAnalytics] = useState<EntityAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<string>('7d');
  const [sortBy, setSortBy] = useState<string>('total_clicks');
  const { toast } = useToast();

  useEffect(() => {
    loadAnalytics();
  }, [siteId, timeRange, sortBy]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await authFetch(
        `/qa/sites/${siteId}/entity-analytics?timeRange=${timeRange}&sortBy=${sortBy}&limit=20`
      );
      const data = await response.json();
      if (data.success) {
        setAnalytics(data.analytics || []);
      }
    } catch (error) {
      console.error('Failed to load entity analytics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load entity analytics',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getTotalMetrics = () => {
    const totalClicks = analytics.reduce((sum, item) => sum + item.total_clicks, 0);
    const totalUniqueUsers = new Set(analytics.map(item => item.unique_users)).size;
    const avgConversion = analytics.length > 0
      ? analytics.reduce((sum, item) => sum + item.conversion_rate, 0) / analytics.length
      : 0;

    return { totalClicks, totalUniqueUsers, avgConversion };
  };

  const getEntityTypeColor = (type: string) => {
    switch (type) {
      case 'product':
        return 'bg-blue-600';
      case 'category':
        return 'bg-green-600';
      case 'brand':
        return 'bg-purple-600';
      case 'article':
        return 'bg-orange-600';
      default:
        return 'bg-gray-600';
    }
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  const metrics = getTotalMetrics();
  const maxClicks = Math.max(...analytics.map(a => a.total_clicks), 1);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Entity Clicks</CardTitle>
            <MousePointerClick className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalClicks.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Across {analytics.length} entities
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unique Visitors</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalUniqueUsers.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Engaged with linked content
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Conversion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.avgConversion.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              Click to action rate
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Entity Link Analytics</CardTitle>
              <CardDescription>
                Track entity clicks and user engagement with linked content
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">Last 24 hours</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="total_clicks">Most Clicks</SelectItem>
                  <SelectItem value="unique_users">Most Users</SelectItem>
                  <SelectItem value="conversion_rate">Best Conversion</SelectItem>
                  <SelectItem value="last_clicked">Most Recent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : analytics.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No entity analytics data available for this time period</p>
              <p className="text-sm mt-2">Entity links will appear here once users interact with them</p>
            </div>
          ) : (
            <div className="space-y-4">
              {analytics.map((entity) => {
                const clickPercentage = (entity.total_clicks / maxClicks) * 100;

                return (
                  <div key={entity.entity_id} className="rounded-lg border p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium">{entity.entity_name}</h4>
                          <Badge variant="secondary" className={getEntityTypeColor(entity.entity_type)}>
                            {entity.entity_type}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Last clicked: {new Date(entity.last_clicked).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">{entity.total_clicks}</div>
                        <div className="text-xs text-muted-foreground">clicks</div>
                      </div>
                    </div>

                    {/* Click Volume Bar */}
                    <div className="mb-3">
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-primary rounded-full h-2 transition-all"
                          style={{ width: `${clickPercentage}%` }}
                        />
                      </div>
                    </div>

                    {/* Metrics Grid */}
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Unique Users</p>
                        <p className="font-medium">{entity.unique_users}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Avg Time</p>
                        <p className="font-medium">{formatDuration(entity.avg_time_on_page)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Conversion</p>
                        <p className="font-medium">{entity.conversion_rate.toFixed(1)}%</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Entity Type Breakdown */}
      {analytics.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Entity Type Breakdown</CardTitle>
            <CardDescription>
              Distribution of clicks by entity type
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(
                analytics.reduce((acc, item) => {
                  acc[item.entity_type] = (acc[item.entity_type] || 0) + item.total_clicks;
                  return acc;
                }, {} as Record<string, number>)
              )
                .sort(([, a], [, b]) => b - a)
                .map(([type, clicks]) => {
                  const percentage = (clicks / metrics.totalClicks) * 100;
                  return (
                    <div key={type} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className={getEntityTypeColor(type)}>
                            {type}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-muted-foreground">{clicks} clicks</span>
                          <span className="font-medium">{percentage.toFixed(1)}%</span>
                        </div>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className={`${getEntityTypeColor(type)} rounded-full h-2 transition-all`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
